import { useContext, useEffect, useState, useRef } from "react";
import { IColumn } from "@/manager/components/data-table/data-table";
import SimpleModal from "@/shared/components/simple-modal/simple-modal";
import "./advanced-search-config.scss";
import useHttp from "@/shared/hooks/use-http";
import { getAllData, getData, postSingle } from "@/shared/utils/api";
import AuthContext from "@/shared/context/auth-context";
import { useHttpGet } from "@/shared/hooks/use-httpNonTable";
import ColumnSelector from "@/manager/components/column-selector/column-selector";
import Spinner from "@/shared/components/spinner/spinner";
import { createUUID, validateLoadingStatusNew } from "@/shared/utils/utils";
import Collapsible from "@/manager/components/collapsible/collapsible";
import { IDataType } from "@/shared/definitions/datatypes";
import { mapDatatype } from "@/shared/definitions/datatype-mapping";

export interface IAdvancedSearchConfigProps {
  show: boolean;
  onPopupClosed(): void;
  onSave: (selectedConfig: ISelectedConfig) => void;
  savedConfig?: ISelectedConfig;
  onPostSave: (success: boolean) => void;
}

export interface ISelectedConfig {
  alertcase: IColumn[];
  notes: IColumn[];
  documents: IColumn[];
  entities: { [key: string]: { name: string; columns: IColumn[] } };
}

interface IDataModelItem {
  id: string;
  name: string;
  isOpen: boolean;
  columns?: IColumn[];
  selectedColumns?: IColumn[];
}

interface IDatamodelField {
  id: string;
  input_field_name: string;
  field_name: string;
  field_datatype: string;
  empty_values: number;
  undefined_values: number;
  unique_values: number;
  text_type: string;
  primary_key: boolean;
}

interface IDataModelDetails {
  id: string;
  name: string;
  fields: IDatamodelField[];
}

interface IAdvancedSearchFieldPayload {
  id: string;
  model_type: string;
  model_id?: string | undefined;
  field_name?: string | undefined;
  field_id?: string | undefined;
  field_type?: string;
}

const documentColumns: IColumn[] = [
  {
    key: "file_name",
    displayName: "File name",
    dataType: {
      type: "text",
    },
  },
  {
    key: "file_type",
    displayName: "File type",
    dataType: {
      type: "text",
    },
  },
  {
    key: "date",
    displayName: "Uploaded date",
    dataType: {
      type: "dateTime",
    },
  },
  {
    key: "content",
    displayName: "Content",
    dataType: {
      type: "text",
    },
  },
];

const notesColumns: IColumn[] = [
  {
    key: "title",
    displayName: "Title",
    dataType: {
      type: "text",
    },
  },
  {
    key: "content",
    displayName: "Content",
    dataType: {
      type: "text",
    },
  },
  {
    key: "createdByName",
    displayName: "Created by",
    dataType: {
      type: "text",
    },
  },
  {
    key: "createdAt",
    displayName: "Created at",
    dataType: {
      type: "dateTime",
    },
  },
];

const AdvancedSearchConfig = ({
  show,
  onPopupClosed: handlePopupClosed,
  onSave: onSave,
  onPostSave,
}: IAdvancedSearchConfigProps) => {
  const [showModal, setShowModal] = useState<boolean>(show);
  const authCtx = useContext(AuthContext);
  const [managerConfig, setManagerConfig] = useState<{
    id: string;
    manager_alertcase_id: string;
    suspicious_definition: string | undefined;
    suspicious_negative: string | undefined;
    suspicious_positive: string | undefined;
  }>({
    id: "",
    manager_alertcase_id: "",
    suspicious_definition: undefined,
    suspicious_negative: undefined,
    suspicious_positive: undefined,
  });
  const [selectedAlertCaseColumns, setSelectedAlertCaseColumns] = useState<IColumn[]>([]);
  const [selectedNotesColumns, setSelectedNotesColumns] = useState<IColumn[]>([]);
  const [selectedDocumentColumns, setSelectedDocumentColumns] = useState<IColumn[]>([]);
  const [alertCaseColumns, setAlertCaseColumns] = useState<IColumn[]>([]);
  const [entitiesList, setEntitiesList] = useState<IDataModelItem[]>([]);
  const [loadedSections, setLoadedSections] = useState<{ alertcase: boolean; entities: boolean }>({ alertcase: false, entities: false });
  const savedEntitySelectionsRef = useRef<{ [key: string]: IColumn[] }>({});

  const { sendRequest: getAllEntities, status: getAllEntitiesStatus, data: getAllEntitiesData, error: getAllEntitiesError } = useHttp(getAllData, false, authCtx.token);
  const { sendRequest: getAlertCase, status: getAlertCaseStatus, data: getAlertCaseData } = useHttp(getAllData, false, authCtx.token, "alertcase");
  const { sendRequest: getManagerConfig, status: getManagerConfigStatus, data: getManagerConfigData, error: getManagerConfigError } = useHttpGet(getData, false, authCtx.token, undefined, "manager");
  const { sendRequest: getEntity, data: getEntityData, status: getEntityStatus, error: getEntityError } = useHttp(getAllData, false, authCtx.token, "entity");
  const { sendRequest: postAdvancedSearchFields, status: postAdvancedSearchFieldsStatus } = useHttp(postSingle, true, authCtx.token, "advancedSearchConfig", "manager");
  const { sendRequest: getAdvancedSearchFields, data: getAdvancedSearchFieldsData, status: getAdvancedSearchFieldsStatus, error: getAdvancedSearchFieldsError} = useHttp(getAllData, false, authCtx.token, "advancedSearchConfig", "manager");

  useEffect(() => {
    onPostSave && onPostSave(true);
  }, [postAdvancedSearchFieldsStatus]);

  useEffect(() => {
    if (show) {
      getAdvancedSearchFields("custom_search_fields");
      getManagerConfig("configuration");
    }
  }, [show]);

  useEffect(() => {
    if (getAdvancedSearchFieldsStatus === "completed" && getAdvancedSearchFieldsData && !getAdvancedSearchFieldsError) {
      const fields = getAdvancedSearchFieldsData.rowData;

      if (!fields || !Array.isArray(fields) || fields.length === 0) {
        return;
      }

      const notesFields = fields.filter(
        (f: IAdvancedSearchFieldPayload) => f.model_type === "notes"
      );
      setSelectedNotesColumns(
        notesFields.map((f: {
          id: string;
          model_type: string;
          model_id: string | undefined;
          field_name: string;
          field_id: string;
          field_type: string;
        }) => ({
          key: generateKeyFromFieldName(f.field_name),
          displayName: f.field_name,
          dataType: mapDatatype(f.field_type),
        }))
      );

      const documentFields = fields.filter(
        (f: IAdvancedSearchFieldPayload) => f.model_type === "documents"
      );
      setSelectedDocumentColumns(
        documentFields.map((f: {
          id: string;
          model_type: string;
          model_id: string | undefined;
          field_name: string;
          field_id: string;
          field_type: string;
        }) => ({
          key: generateKeyFromFieldName(f.field_name),
          displayName: f.field_name,
          dataType: mapDatatype(f.field_type),
        }))
      );
      const alertcaseFields = fields.filter(
        (f: IAdvancedSearchFieldPayload) => f.model_type === "manager_alertcase" || f.model_type === "alertcase"
      );

      setSelectedAlertCaseColumns(
        alertcaseFields.map((f: {
          id: string;
          model_type: string;
          model_id: string | undefined;
          field_name: string;
          field_id: string;
          field_type: string;
        }) => {
          const allowedTypes: IDataType["type"][] = ["text", "number", "dateTime"];
          const fieldType = allowedTypes.includes(f.field_type as IDataType["type"])
            ? (f.field_type as IDataType["type"])
            : "text";

          return {
            key: f.field_id,
            displayName: f.field_name,
            dataType: { type: fieldType },
          } as IColumn;
        })
      );

      const entityFields = fields.filter((f: IAdvancedSearchFieldPayload) => f.model_type === "entity");
      const entitySelections: { [key: string]: IColumn[] } = {};
      entityFields.forEach((f: {
        id: string;
        model_type: string;
        model_id: string;
        field_name: string;
        field_id: string;
        field_type: string;
      }) => {
        if (!entitySelections[f.model_id]) {
          entitySelections[f.model_id] = [];
        }
        const allowedTypes: IDataType["type"][] = ["text", "number", "dateTime"];
        const fieldType = allowedTypes.includes(f.field_type as IDataType["type"])
          ? (f.field_type as IDataType["type"])
          : "text";

        entitySelections[f.model_id].push({
          key: f.field_id,
          displayName: f.field_name,
          dataType: {
            type: fieldType as IDataType["type"],
          } as IDataType,
        });
      });
      savedEntitySelectionsRef.current = entitySelections;
    }
  }, [getAdvancedSearchFieldsStatus, getAdvancedSearchFieldsData, getAdvancedSearchFieldsError]);

  useEffect(() => {
    if (getAlertCaseStatus === "completed" && getAlertCaseData && getAlertCaseData.rowData) {
      const columns = getAlertCaseData.rowData.map((field: IDatamodelField) => ({
        key: field.id,
        displayName: field.field_name,
        dataType: {
          type: field.field_datatype || "text"
        },
      }));
      setAlertCaseColumns([
        ...columns,
        {
          key: "alertcase_number",
          displayName: "AlertCase number",
          dataType: { type: "text" },
        },
        {
          key: "title",
          displayName: "Title",
          dataType: { type: "text" },
        },
        {
          key: "creation_date",
          displayName: "Creation date",
          dataType: { type: "dateTime" },
        },
        {
          key: "last_changed_date",
          displayName: "Last changed date",
          dataType: { type: "dateTime" },
        },
        {
          key: "process_type_id",
          displayName: "Process type",
          dataType: { type: "text" },
        },
        {
          key: "domain_type_id",
          displayName: "Domain type",
          dataType: { type: "text" },
        },
        {
          key: "consolidate_on_entity_type",
          displayName: "Consolidation",
          sortable: false,
          dataType: { type: "text" },
        },
        {
          key: "current_stage",
          displayName: "Stage",
          dataType: { type: "text" },
        },
        {
          key: "created_by",
          displayName: "Created by",
          sortable: false,
          dataType: { type: "text" },
        },
        {
          key: "last_changed_by_id",
          displayName: "Last changed by",
          sortable: false,
          dataType: { type: "text" },
        },
        {
          key: "assigned_to",
          displayName: "Assigned to",
          sortable: false,
          dataType: { type: "text" },
        },
        {
          key: "state",
          displayName: "Status",
          dataType: { type: "text" },
        },
      ]);
    }
  }, [getAlertCaseStatus, getAlertCaseData]);

  useEffect(() => {
    if (getAllEntitiesStatus === "completed" && getAllEntitiesData && !getAllEntitiesError) {
      const entities: IDataModelItem[] = getAllEntitiesData.rows.map(
        (entity: IDataModelDetails) => ({
          id: entity.id,
          name: entity.name,
          isOpen: false,
          columns: [],
          selectedColumns: savedEntitySelectionsRef.current[entity.id] || [],
        })
      );
      setEntitiesList(entities);
    }
  }, [getAllEntitiesStatus, getAllEntitiesData, getAllEntitiesError]);

  useEffect(() => {
    if (getEntityStatus === "completed" && getEntityData && !getEntityError) {
      const columns = getEntityData.rowData.map((field: IDatamodelField) => ({
        key: field.id,
        displayName: field.field_name,
        dataType: {
          type: field.field_datatype || "text",
        },
      }));

      setEntitiesList((prev) =>
        prev.map((entity) => {
          if (entity.isOpen && !entity.columns?.length) {
            return { ...entity, columns };
          }
          return entity;
        })
      );
    }
  }, [getEntityStatus, getEntityData, getEntityError]);

  useEffect(() => {
    if (getManagerConfigStatus === "completed" && getManagerConfigData && !getManagerConfigError) {
      setManagerConfig(getManagerConfigData);
    }
  }, [getManagerConfigStatus, getManagerConfigData, getManagerConfigError]);

  useEffect(() => {
    setShowModal(show);
  }, [show]);

  useEffect(() => {
    if (getAllEntitiesStatus === "completed" && getAllEntitiesData && !getAllEntitiesError) {
    const entityWithSelections = entitiesList.find(
      (entity) => entity.selectedColumns && entity.selectedColumns.length > 0
    );

    if (entityWithSelections && (!entityWithSelections.columns || entityWithSelections.columns.length === 0)) {
      getEntity(`datamodel/entity/${entityWithSelections.id}`);
      setEntitiesList((prev) =>
        prev.map((entity) =>
          entity.id === entityWithSelections.id
            ? { ...entity, isOpen: true }
            : entity
        )
      );
    }
  }
  }, [getAllEntitiesData, getAllEntitiesStatus, getAllEntitiesError]);

  useEffect(() => {
    if (managerConfig?.manager_alertcase_id) {
      getAlertCase(`datamodel/alertcase/${managerConfig.manager_alertcase_id}`);
    }
  }, [managerConfig?.manager_alertcase_id]);

  useEffect(() => {
    if (getAlertCaseStatus === "completed" && !loadedSections.alertcase) {
      setLoadedSections((prev) => ({ ...prev, alertcase: true }));
    }
  }, [getAlertCaseStatus, loadedSections.alertcase]);

  useEffect(() => {
    const hasSavedEntitySelections =
      Object.keys(savedEntitySelectionsRef.current).length > 0;

    if (hasSavedEntitySelections && !loadedSections.entities) {
      getAllEntities("datamodel/entities", "?archived=false");
      setLoadedSections((prev) => ({ ...prev, entities: true }));
    }
  }, [savedEntitySelectionsRef.current]);

  const handleAlertCaseColumnSelectionChanged = (selectedColumns: IColumn[]) => {
    const columns: IColumn[] = selectedColumns.map((column) => {
      const fieldDataType = alertCaseColumns.find(
        (field) => field.key === column.key
      )?.dataType;
      return {
        key: column.key,
        displayName: column.displayName,
        dataType: fieldDataType || { type: "text" },
      } as IColumn;
    });

    setSelectedAlertCaseColumns(columns);
  };

  const generateKeyFromFieldName = (field_name: string): string => {
    return field_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  const handleNotesColumnSelectionChanged = (selectedColumns: IColumn[]) => {
    const columns: IColumn[] = selectedColumns.map((column) => {
      const fieldDataType = notesColumns.find(
        (field) => field.key === column.key
      )?.dataType;
      return {
        key: column.key,
        displayName: column.displayName,
        dataType: fieldDataType || { type: "text" },
      } as IColumn;
    });
    setSelectedNotesColumns(columns);
  };

  const handleDocumentColumnSelectionChanged = (selectedColumns: IColumn[]) => {
    const columns: IColumn[] = selectedColumns.map((column) => {
      const fieldDataType = documentColumns.find(
        (field) => field.key === column.key
      )?.dataType;

      return {
        key: column.key,
        displayName: column.displayName,
        dataType: fieldDataType || { type: "text" },
      } as IColumn;
    });

    setSelectedDocumentColumns(columns);
  };

  const handleEntityColumnSelectionChanged = (entityId: string, selectedColumns: IColumn[]) => {
    const columns: IColumn[] = selectedColumns.map((column) => {
      return {
        key: column.key,
        displayName: column.displayName,
      } as IColumn;
    });

    setEntitiesList((prev) =>
      prev.map((entity) =>
        entity.id === entityId
          ? { ...entity, selectedColumns: columns }
          : entity
      )
    );
  };

  const handleAlertCaseToggle = (isOpen: boolean) => {
    if (isOpen && !loadedSections.alertcase && managerConfig?.manager_alertcase_id) {
      getAlertCase(`datamodel/alertcase/${managerConfig.manager_alertcase_id}`);
      setLoadedSections((prev) => ({ ...prev, alertcase: true }));
    }
  };

  const handleEntitiesToggle = (isOpen: boolean) => {
    if (isOpen && !loadedSections.entities) {
      getAllEntities("datamodel/entities", "?archived=false");
      setLoadedSections((prev) => ({ ...prev, entities: true }));
    }
  };

  const handleEntityItemToggle = (entityId: string, isOpen: boolean) => {
    setEntitiesList((prev) =>
      prev.map((entity) => {
        if (entity.id === entityId) {
          if (isOpen && (!entity.columns || entity.columns.length === 0)) {
            getEntity(`datamodel/entity/${entityId}`);
          }
          return { ...entity, isOpen };
        }
        return entity;
      })
    );
  };

  interface IParseColumnsInput {
    key: string;
    displayName: string;
    dataType: {
      type: string;
    };
  }
    
  const handleSaveClick = () => {
    const advancedSearchFields: IAdvancedSearchFieldPayload[] = [];
    const managerAlertcaseFields = [
      { key: "alertcase_number", type: "text", displayName: "AlertCase number" },
      { key: "title", type: "text", displayName: "Title" },
      { key: "creation_date", type: "dateTime", displayName: "Creation date" },
      { key: "last_changed_date", type: "dateTime", displayName: "Last changed date" },
      { key: "process_type_id", type: "text", displayName: "Process type" },
      { key: "domain_type_id", type: "text", displayName: "Domain type" },
      { key: "consolidate_on_entity_type", type: "text", displayName: "Consolidation" },
      { key: "current_stage", type: "text", displayName: "Stage" },
      { key: "created_by", type: "text", displayName: "Created by" },
      { key: "last_changed_by_id", type: "text", displayName: "Last changed by" },
      { key: "state", type: "text", displayName: "Status" },
      { key: "assigned_to", type: "text", displayName: "Assigned to" },
    ];
  const existingFields = getAdvancedSearchFieldsData?.rowData || [];
  const findExistingFieldId = (modelType: string, fieldId: string, modelId?: string): string | undefined => {
    const existing = existingFields.find((f: IAdvancedSearchFieldPayload) => {
      if (modelType === "entity") {
        return f.model_type === modelType && 
               f.field_id === fieldId && 
               f.model_id === modelId;
      }
      return f.model_type === modelType && f.field_id === fieldId;
    });
    return existing?.id;
  };

  selectedAlertCaseColumns.forEach((column) => {
    const managerField = managerAlertcaseFields.find(
      (f) => f.key === column.key
    );
    if (managerField) {
      const existingId = findExistingFieldId("manager_alertcase", managerField.key);
      advancedSearchFields.push({
        id: existingId || createUUID(),
        model_type: "manager_alertcase",
        model_id: undefined,
        field_name: managerField.displayName,
        field_id: managerField.key,
        field_type: managerField.type || "text",
      });
    } else {
      const existingId = findExistingFieldId("alertcase", column.key, managerConfig.manager_alertcase_id);
      advancedSearchFields.push({
        id: existingId || createUUID(),
        model_type: "alertcase",
        model_id: managerConfig.manager_alertcase_id,
        field_name: column.displayName,
        field_id: column.key,
        field_type: column.dataType?.type || "text",
      });
    }
  });

  const parseColumns = (columns: IParseColumnsInput[], type: string): void => {
    columns.forEach((column) => {
      const existingId = findExistingFieldId(type, column.key);
      advancedSearchFields.push({
        id: existingId || createUUID(),
        model_type: type,
        model_id: undefined,
        field_name: column.displayName,
        field_id: column.key,
        field_type: column.dataType.type || "text",
      });
    });
  };

  parseColumns(selectedNotesColumns, "notes");
  parseColumns(selectedDocumentColumns, "documents");

  entitiesList.forEach((entity) => {
    if (entity.selectedColumns && entity.selectedColumns.length > 0) {
      entity.selectedColumns.forEach((column) => {
        const existingId = findExistingFieldId("entity", column.key, entity.id);
        advancedSearchFields.push({
          id: existingId || createUUID(),
          model_type: "entity",
          model_id: entity.id,
          field_name: column.displayName,
          field_id: column.key,
          field_type: undefined,
        });
      });
    }
  });

  const payload = {
    rowData: advancedSearchFields,
  };

  postAdvancedSearchFields("custom_search_fields", payload);

  if (onSave) {
    const entitiesConfig: {[key: string]: { name: string; columns: IColumn[] }} = {};
    entitiesList.forEach((entity) => {
      if (entity.selectedColumns && entity.selectedColumns.length > 0) {
        entitiesConfig[entity.id] = {
          name: entity.name,
          columns: entity.selectedColumns,
        };
      }
    });

    const config: ISelectedConfig = {
      alertcase: selectedAlertCaseColumns,
      notes: selectedNotesColumns,
      documents: selectedDocumentColumns,
      entities: entitiesConfig,
    };

    onSave(config);
  }
  handlePopupClosed();
};

  const modalContent = () => {
    return (
      <div className="advancedSearchConfig">
        {validateLoadingStatusNew([getAdvancedSearchFieldsStatus, getAlertCaseStatus, getAllEntitiesStatus, getEntityStatus]) && <Spinner />}
        <p className="advancedSearchConfig-description">
          Select which fields you want to see in the advanced search results.
        </p>
        <Collapsible
          title="Alertcase fields"
          fieldCount={selectedAlertCaseColumns.length}
          defaultOpen={selectedAlertCaseColumns.length > 0}
          onToggle={handleAlertCaseToggle}
        >
          <ColumnSelector
            availableColumns={alertCaseColumns}
            selectedColumns={selectedAlertCaseColumns}
            onSelectionChanged={handleAlertCaseColumnSelectionChanged}
          />
        </Collapsible>

        <Collapsible
          title="Notes"
          fieldCount={selectedNotesColumns.length}
          defaultOpen={selectedNotesColumns.length > 0}
        >
          <ColumnSelector
            availableColumns={notesColumns}
            selectedColumns={selectedNotesColumns}
            onSelectionChanged={handleNotesColumnSelectionChanged}
          />
        </Collapsible>

        <Collapsible
          title="Documents"
          fieldCount={selectedDocumentColumns.length}
          defaultOpen={selectedDocumentColumns.length > 0}
        >
          <ColumnSelector
            availableColumns={documentColumns}
            selectedColumns={selectedDocumentColumns}
            onSelectionChanged={handleDocumentColumnSelectionChanged}
          />
        </Collapsible>

        <Collapsible
          title="Consolidating entities"
          fieldCount={entitiesList.reduce(
            (acc, entity) => acc + (entity.selectedColumns?.length || 0),
            0
          )}
          defaultOpen={false}
          onToggle={handleEntitiesToggle}
        >
          {entitiesList.map((entity) => {
            return (
              <Collapsible
                key={entity.id}
                title={entity.name}
                fieldCount={entity.selectedColumns?.length}
                defaultOpen={false}
                onToggle={(isOpen) => handleEntityItemToggle(entity.id, isOpen)}
              >
                {entity.columns && entity.columns.length > 0 && (
                  <ColumnSelector
                    availableColumns={entity.columns}
                    selectedColumns={entity.selectedColumns || []}
                    onSelectionChanged={(selected) =>
                      handleEntityColumnSelectionChanged(entity.id, selected)
                    }
                  />
                )}
              </Collapsible>
            );
          })}
        </Collapsible>
      </div>
    );
  };

  return (
    <SimpleModal
      modalBody={modalContent()}
      mode={"Advanced Search Configuration"}
      handleModalClosed={handlePopupClosed}
      handleSubmit={handleSaveClick}
      show={showModal}
      buttonText="Save"
      showCancelButton={true}
    />
  );
};

export default AdvancedSearchConfig;
